import { World } from '../sim/world'
import { Enemy, Rarity, Stats, WeaponState } from '../sim/types'
import { makeProjectile, rollDamage, refreshOrbitals } from '../sim/combat'
import { nearestEnemy, randomEnemy } from '../sim/aim'
import { TAU } from '../core/math'

/**
 * How a weapon picks the foe it aims at, resolved once per shot in fireWeapons:
 *  - 'nearest'  → closest enemy within aimRange (rapid / tracking weapons)
 *  - 'toughest' → highest-hp enemy within aimRange (heavy single-shots seek bosses/elites)
 *  - 'forward'  → no auto-target; the weapon fires along player.facing or aims itself
 * Weapons flagged requiresTarget skip the shot (and keep their cooldown) when no target
 * is in range, so heavy weapons never whiff into empty space.
 */
export type AimPolicy = 'nearest' | 'toughest' | 'forward'

export interface WeaponContext {
  world: World
  ws: WeaponState
  level: number
  stats: Stats
  /** Target resolved by the weapon's aim policy; null for 'forward'/self/scatter weapons. */
  target: Enemy | null
}

export interface WeaponDef {
  id: string
  name: string
  icon: string
  rarity: Rarity
  maxLevel: number
  describe(level: number): string
  /** Base cooldown in seconds at a given level (before attackSpeed is applied). */
  baseCooldown(level: number): number
  fire(ctx: WeaponContext): void
  /** Targeting policy used to resolve ctx.target before fire(). Default 'forward' (no target). */
  aim?: AimPolicy
  /** Search radius for the aim policy. Default Infinity. */
  aimRange?: number
  /** When true, the shot is skipped (cooldown preserved) if the aim policy finds no target. */
  requiresTarget?: boolean
  /** Evolution: when at max level AND owning evolveItem, can evolve into evolvesTo. */
  evolvesTo?: string
  evolveItem?: string
  /** Hidden weapons are only obtainable via evolution, not the level-up pool. */
  hidden?: boolean
}

// helper: scale a base number per level (level 1 = base)
function lv(base: number, perLevel: number, level: number): number {
  return base + perLevel * (level - 1)
}

export const WEAPON_LIST: WeaponDef[] = [
  // ---- Boomeraxe (default starter): arcs out and returns, pierces ----
  {
    id: 'boomeraxe',
    name: 'Boomeraxe',
    icon: '🪓',
    rarity: 'common',
    maxLevel: 8,
    describe: (l) => `Hurl an axe that arcs out and back, piercing all it hits. +1 axe at lv 3/6.`,
    baseCooldown: (l) => Math.max(0.5, 1.15 - 0.05 * l),
    aim: 'nearest',
    fire(ctx) {
      const { world, target } = ctx
      const count = 1 + (ctx.level >= 3 ? 1 : 0) + (ctx.level >= 6 ? 1 : 0) + ctx.stats.projectileCount
      const base = lv(12, 4, ctx.level)
      const baseAngle = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.player.facing
      for (let i = 0; i < count; i++) {
        const spread = count > 1 ? (i - (count - 1) / 2) * 0.35 : 0
        const a = baseAngle + spread
        const d = rollDamage(world, base)
        const speed = 14 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'boomeraxe',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            behavior: 'boomerang',
            damage: d.amount,
            crit: d.crit,
            life: 2.2 * ctx.stats.duration,
            pierce: 999,
            radius: 0.55 * ctx.stats.area,
            color: 0xc9d4e0,
            knockback: 1.2,
            hitCooldown: 0.25,
            angle: a,
          }),
        )
      }
    },
    evolvesTo: 'stormglaive',
    evolveItem: 'gale_charm',
  },

  // ---- Pebble Revolver: rapid straight shots at nearest ----
  {
    id: 'revolver',
    name: 'Pebble Revolver',
    icon: '🔫',
    rarity: 'common',
    maxLevel: 8,
    describe: () => `Fire fast bullets at the nearest foe. Extra bullets and pierce as it levels.`,
    baseCooldown: (l) => Math.max(0.18, 0.6 - 0.05 * l),
    aim: 'nearest',
    aimRange: 22,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const count = 1 + Math.floor(ctx.level / 3) + ctx.stats.projectileCount
      const base = lv(6, 2.2, ctx.level)
      const baseAngle = Math.atan2(target.z - world.player.z, target.x - world.player.x)
      for (let i = 0; i < count; i++) {
        const spread = count > 1 ? (i - (count - 1) / 2) * 0.12 : 0
        const a = baseAngle + spread
        const d = rollDamage(world, base)
        const speed = 22 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'revolver',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            behavior: 'straight',
            damage: d.amount,
            crit: d.crit,
            life: 1.4 * ctx.stats.duration,
            pierce: Math.floor(ctx.level / 4),
            radius: 0.3 * ctx.stats.area,
            color: 0xffe27a,
            knockback: 0.5,
          }),
        )
      }
    },
    evolvesTo: 'gatling',
    evolveItem: 'hot_sauce',
  },

  // ---- Halo Orbs: orbiting projectiles that persist ----
  {
    id: 'halo',
    name: 'Halo Orbs',
    icon: '🪐',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Orbs spin around you, smashing anything they touch. More orbs as it levels.`,
    baseCooldown: (l) => 2.4, // refresh interval — orbs persist between ticks (MP3)
    fire(ctx) {
      refreshOrbitals(ctx.world, ctx.ws, {
        ownerWeapon: 'halo',
        behavior: 'orbit',
        count: 2 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount,
        damageBase: lv(8, 3, ctx.level),
        radius: 0.5 * ctx.stats.area,
        color: 0x7ad7ff,
        orbitRadius: (2.4 + ctx.level * 0.12) * ctx.stats.area,
        orbitSpeed: 3.2 * ctx.stats.projectileSpeed,
        knockback: 0.6,
        hitCooldown: 0.4,
      })
    },
    evolvesTo: 'ringofsaturn',
    evolveItem: 'gyroscope',
  },

  // ---- Frost Pulse: aura around player that damages + slows ----
  {
    id: 'frostpulse',
    name: 'Frost Pulse',
    icon: '❄️',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `A chilling aura damages and slows nearby foes. Radius grows with level.`,
    baseCooldown: () => 0.5,
    fire(ctx) {
      const { world } = ctx
      const base = lv(3, 1.3, ctx.level)
      const radius = (2.0 + ctx.level * 0.25) * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'frostpulse',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.5,
          pierce: 999,
          radius,
          color: 0x9fe8ff,
          slow: 0.9,
          knockback: 0,
          hitCooldown: 0.5,
        }),
      )
    },
    evolvesTo: 'blizzard',
    evolveItem: 'frozen_heart',
  },

  // ---- Meteor: lobbed projectile that explodes in an area ----
  {
    id: 'meteor',
    name: 'Meteor',
    icon: '☄️',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `Call a meteor onto a random foe; explodes for heavy area damage and burn.`,
    baseCooldown: (l) => Math.max(1.4, 3.2 - 0.12 * l),
    fire(ctx) {
      const { world } = ctx
      const target = randomEnemy(world, world.player.x, world.player.z, 20) ?? nearestEnemy(world, world.player.x, world.player.z)
      const tx = target ? target.x : world.player.x + world.rng.range(-6, 6)
      const tz = target ? target.z : world.player.z + world.rng.range(-6, 6)
      const base = lv(22, 7, ctx.level)
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'meteor',
          behavior: 'lobbed',
          x: tx,
          z: tz,
          y: 14,
          vy: 0,
          damage: 0, // damage applied on explosion
          crit: d.crit,
          life: 1.2,
          radius: 0.6,
          color: 0xff7a2d,
          explodeRadius: (2.6 + ctx.level * 0.2) * ctx.stats.area,
          explodeDamage: d.amount,
          burn: 3,
          knockback: 1.5,
        }),
      )
    },
    evolvesTo: 'apocalypse',
    evolveItem: 'ember_core',
  },

  // ---- Zapper: chain lightning to nearest then jumps ----
  {
    id: 'zapper',
    name: 'Zapper',
    icon: '⚡',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `Lightning strikes the nearest foe and chains to others nearby.`,
    baseCooldown: (l) => Math.max(0.7, 1.6 - 0.08 * l),
    aim: 'nearest',
    aimRange: 16,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const base = lv(12, 4, ctx.level)
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'zapper',
          behavior: 'straight',
          x: world.player.x,
          z: world.player.z,
          vx: 0,
          vz: 0,
          speed: 0,
          damage: d.amount,
          crit: d.crit,
          life: 0.05, // resolves immediately via chain logic in collision
          pierce: 0,
          radius: 0.5,
          color: 0x9fd0ff,
          chain: 2 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount,
          knockback: 0.3,
          // teleport projectile to target so first hit lands there
          // (collision/chain system reads target by proximity)
        }),
      )
      // place the bolt on the target
      const last = world.projectiles[world.projectiles.length - 1]
      last.x = target.x
      last.z = target.z
    },
    evolvesTo: 'tempest',
    evolveItem: 'capacitor',
  },

  // ---- Sawblades: a spinning blade arc that sweeps around the player ----
  {
    id: 'sawblade',
    name: 'Sawblades',
    icon: '🌀',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Whirling blades sweep around you, shredding the horde.`,
    baseCooldown: () => 1.6,
    fire(ctx) {
      refreshOrbitals(ctx.world, ctx.ws, {
        ownerWeapon: 'sawblade',
        behavior: 'spinner',
        count: 1 + Math.floor(ctx.level / 3) + ctx.stats.projectileCount,
        damageBase: lv(7, 2.6, ctx.level),
        radius: 0.55 * ctx.stats.area,
        color: 0xff5a7a,
        orbitRadius: 3.2 * ctx.stats.area,
        orbitSpeed: 7 * ctx.stats.projectileSpeed,
        knockback: 0.8,
        hitCooldown: 0.3,
      })
    },
  },

  // ---- Scattergun: a cone of pellets at nearest ----
  {
    id: 'scattergun',
    name: 'Scattergun',
    icon: '💥',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Blast a cone of pellets. Devastating up close.`,
    baseCooldown: (l) => Math.max(0.6, 1.3 - 0.06 * l),
    aim: 'nearest',
    aimRange: 14,
    fire(ctx) {
      const { world, target } = ctx
      const baseAngle = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.player.facing
      const pellets = 5 + ctx.level + ctx.stats.projectileCount
      const base = lv(4, 1.4, ctx.level)
      for (let i = 0; i < pellets; i++) {
        const a = baseAngle + (world.rng.next() - 0.5) * 0.7
        const d = rollDamage(world, base)
        const speed = (16 + world.rng.range(-2, 2)) * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'scattergun',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 0.55 * ctx.stats.duration,
            pierce: 0,
            radius: 0.28 * ctx.stats.area,
            color: 0xffcaa0,
            knockback: 0.7,
          }),
        )
      }
    },
  },

  // ---- Drone Swarm: homing missiles ----
  {
    id: 'drones',
    name: 'Drone Swarm',
    icon: '🛸',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `Launch homing drones that seek out enemies.`,
    baseCooldown: (l) => Math.max(0.9, 2.0 - 0.1 * l),
    aim: 'nearest',
    aimRange: 30,
    fire(ctx) {
      const { world, target } = ctx
      const count = 1 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount
      const base = lv(10, 3.5, ctx.level)
      // launch toward the nearest foe (then home); fan out if several drones
      const baseAngle = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.rng.next() * TAU
      for (let i = 0; i < count; i++) {
        const a = baseAngle + (count > 1 ? (i - (count - 1) / 2) * 0.4 : 0)
        const d = rollDamage(world, base)
        const speed = 9 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'drones',
            behavior: 'homing',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 3.5 * ctx.stats.duration,
            pierce: 1,
            radius: 0.35 * ctx.stats.area,
            color: 0x9affc8,
            homingTurn: 5,
            knockback: 0.4,
          }),
        )
      }
    },
  },

  // ---- Holy Tome: pulsing damage book that orbits slowly, big crits ----
  {
    id: 'tome',
    name: 'Holy Tome',
    icon: '📖',
    rarity: 'epic',
    maxLevel: 8,
    describe: () => `A sacred tome orbits you, smiting foes with high-crit bolts of light.`,
    baseCooldown: (l) => Math.max(0.5, 1.1 - 0.06 * l),
    aim: 'nearest',
    aimRange: 18,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const base = lv(14, 5, ctx.level)
      const d = rollDamage(world, base, 0.25) // Tome favors crits (+25% chance) but still gets all damage passives + variance
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'tome',
          behavior: 'homing',
          x: world.player.x,
          z: world.player.z + 0.1,
          vx: 0,
          vz: 0,
          speed: 16 * ctx.stats.projectileSpeed,
          damage: d.amount,
          crit: d.crit,
          life: 2 * ctx.stats.duration,
          pierce: 2,
          radius: 0.4 * ctx.stats.area,
          color: 0xfff0a0,
          homingTurn: 8,
          knockback: 0.4,
        }),
      )
    },
  },

  // ---- Rotsworn: poison aura that clings to the player ----
  {
    id: 'rotaura',
    name: 'Rotsworn',
    icon: '🦠',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `A cloud of rot clings to you, poisoning everything nearby.`,
    baseCooldown: () => 0.6,
    fire(ctx) {
      const { world } = ctx
      const base = lv(2.5, 1.1, ctx.level)
      const radius = (2.2 + ctx.level * 0.22) * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'rotaura',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.6,
          pierce: 999,
          radius,
          color: 0x8fce5a,
          poison: 2.5,
          knockback: 0,
          hitCooldown: 0.6,
        }),
      )
    },
    evolvesTo: 'pestilence',
    evolveItem: 'biohazard',
  },

  // ---- Lash: a whip cracked in the facing direction ----
  {
    id: 'lash',
    name: 'Lash',
    icon: '🔗',
    rarity: 'common',
    maxLevel: 8,
    describe: () => `Crack a whip in the way you face, hitting a wide line. Strikes both ways at lv 5.`,
    baseCooldown: (l) => Math.max(0.5, 1.0 - 0.04 * l),
    aim: 'forward',
    fire(ctx) {
      const { world } = ctx
      // lash is a directional, skill-aimed weapon: always strikes along the way you face
      const face = world.player.facing
      const dirs = ctx.level >= 5 ? [face, face + Math.PI] : [face]
      const base = lv(10, 3.6, ctx.level)
      for (const a of dirs) {
        const d = rollDamage(world, base)
        const speed = 26 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'lash',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 0.22 * ctx.stats.duration,
            pierce: 999,
            radius: (1.3 + ctx.level * 0.05) * ctx.stats.area,
            color: 0xffe27a,
            knockback: 1.4,
            hitCooldown: 0.15,
          }),
        )
      }
    },
    evolvesTo: 'hydralash',
    evolveItem: 'barbed_wire',
  },

  // ---- Caltrops: scattered traps that linger and damage ----
  {
    id: 'caltrops',
    name: 'Caltrops',
    icon: '✴️',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Scatter spiked traps that damage foes who wander into them.`,
    baseCooldown: (l) => Math.max(0.7, 1.6 - 0.07 * l),
    fire(ctx) {
      const { world } = ctx
      const count = 2 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount
      const base = lv(7, 2.6, ctx.level)
      for (let i = 0; i < count; i++) {
        const a = world.rng.next() * TAU
        const r = 1 + world.rng.next() * 3.5
        const d = rollDamage(world, base)
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'caltrops',
            behavior: 'straight',
            x: world.player.x + Math.cos(a) * r,
            z: world.player.z + Math.sin(a) * r,
            vx: 0,
            vz: 0,
            speed: 0,
            damage: d.amount,
            crit: d.crit,
            life: 5 * ctx.stats.duration,
            pierce: 999,
            radius: 0.55 * ctx.stats.area,
            color: 0xccaa55,
            knockback: 0.3,
            hitCooldown: 0.5,
          }),
        )
      }
    },
    evolvesTo: 'minefield',
    evolveItem: 'gunpowder',
  },

  // ---- Ricochet: a ball that bounces between enemies ----
  {
    id: 'ricochet',
    name: 'Ricochet',
    icon: '🎱',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `Fire a ball that ricochets between enemies. More bounces as it levels.`,
    baseCooldown: (l) => Math.max(0.55, 1.3 - 0.06 * l),
    aim: 'nearest',
    aimRange: 20,
    fire(ctx) {
      const { world, target } = ctx
      const a = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.player.facing
      const base = lv(11, 4, ctx.level)
      const d = rollDamage(world, base)
      const speed = 18 * ctx.stats.projectileSpeed
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'ricochet',
          behavior: 'straight',
          x: world.player.x,
          z: world.player.z,
          vx: Math.cos(a) * speed,
          vz: Math.sin(a) * speed,
          speed,
          damage: d.amount,
          crit: d.crit,
          life: 3 * ctx.stats.duration,
          pierce: 0,
          bounce: 3 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount,
          radius: 0.42 * ctx.stats.area,
          color: 0xff9f4a,
          knockback: 0.6,
        }),
      )
    },
    evolvesTo: 'pinball',
    evolveItem: 'rubber_ball',
  },

  // ---- Singularity: a black hole that drags foes in and crushes them ----
  {
    id: 'singularity',
    name: 'Singularity',
    icon: '🕳️',
    rarity: 'epic',
    maxLevel: 8,
    describe: () => `Open a singularity around you that drags foes in and crushes them.`,
    baseCooldown: () => 0.5,
    fire(ctx) {
      const { world } = ctx
      const base = lv(4, 1.6, ctx.level)
      const radius = (2.4 + ctx.level * 0.2) * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'singularity',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.5,
          pierce: 999,
          radius,
          color: 0x9b6cff,
          pull: 6 + ctx.level * 0.5,
          knockback: 0,
          hitCooldown: 0.4,
        }),
      )
    },
    evolvesTo: 'eventhorizon',
    evolveItem: 'dark_matter',
  },

  // ---- Railgun: a charged piercing shot that skewers a whole line ----
  {
    id: 'railgun',
    name: 'Railgun',
    icon: '🔩',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `A charged shot that pierces every enemy in a line. Seeks the toughest foe; never wasted on empty space.`,
    baseCooldown: (l) => Math.max(1.0, 2.4 - 0.1 * l),
    aim: 'toughest',
    aimRange: 40,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      const a = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.player.facing
      const base = lv(28, 9, ctx.level)
      const d = rollDamage(world, base)
      const speed = 40 * ctx.stats.projectileSpeed
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'railgun',
          behavior: 'straight',
          x: world.player.x,
          z: world.player.z,
          vx: Math.cos(a) * speed,
          vz: Math.sin(a) * speed,
          speed,
          damage: d.amount,
          crit: d.crit,
          life: 1.6 * ctx.stats.duration,
          pierce: 999,
          radius: 0.5 * ctx.stats.area,
          color: 0x66ffff,
          knockback: 1.2,
        }),
      )
    },
    evolvesTo: 'annihilator',
    evolveItem: 'tungsten_rod',
  },

  // ---- Spore Cloud: a lingering poison field on a distant foe ----
  {
    id: 'sporecloud',
    name: 'Spore Cloud',
    icon: '🍄',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Burst a cloud of toxic spores that lingers and poisons all within.`,
    baseCooldown: (l) => Math.max(1.0, 2.2 - 0.08 * l),
    fire(ctx) {
      const { world } = ctx
      const target =
        randomEnemy(world, world.player.x, world.player.z, 18) ??
        nearestEnemy(world, world.player.x, world.player.z)
      const tx = target ? target.x : world.player.x + world.rng.range(-5, 5)
      const tz = target ? target.z : world.player.z + world.rng.range(-5, 5)
      const base = lv(3, 1.2, ctx.level)
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'sporecloud',
          behavior: 'straight',
          x: tx,
          z: tz,
          vx: 0,
          vz: 0,
          speed: 0,
          damage: d.amount,
          crit: d.crit,
          life: 4 * ctx.stats.duration,
          pierce: 999,
          radius: (2 + ctx.level * 0.2) * ctx.stats.area,
          color: 0x9acd32,
          poison: 3,
          knockback: 0,
          hitCooldown: 0.6,
        }),
      )
    },
    evolvesTo: 'miasma',
    evolveItem: 'fungal_spores',
  },

  // ---- Frost Lance: piercing icicles that slow and briefly freeze ----
  {
    id: 'frostlance',
    name: 'Frost Lance',
    icon: '🗡️',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Hurl piercing icicles that slow and briefly freeze what they strike.`,
    baseCooldown: (l) => Math.max(0.5, 1.1 - 0.05 * l),
    aim: 'nearest',
    aimRange: 22,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const count = 1 + Math.floor(ctx.level / 3) + ctx.stats.projectileCount
      const base = lv(9, 3, ctx.level)
      const baseAngle = Math.atan2(target.z - world.player.z, target.x - world.player.x)
      for (let i = 0; i < count; i++) {
        const spread = count > 1 ? (i - (count - 1) / 2) * 0.14 : 0
        const a = baseAngle + spread
        const d = rollDamage(world, base)
        const speed = 20 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'frostlance',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 1.4 * ctx.stats.duration,
            pierce: 3 + Math.floor(ctx.level / 2),
            radius: 0.34 * ctx.stats.area,
            color: 0xaee5ff,
            slow: 0.5,
            freeze: 0.5 * ctx.stats.duration,
            knockback: 0.5,
          }),
        )
      }
    },
    evolvesTo: 'glacialspear',
    evolveItem: 'permafrost',
  },

  // ---- Solar Flare: a searing fire aura that ignites nearby foes ----
  {
    id: 'solarflare',
    name: 'Solar Flare',
    icon: '🔆',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `A searing solar aura scorches nearby foes and sets them ablaze.`,
    baseCooldown: () => 0.55,
    fire(ctx) {
      const { world } = ctx
      const base = lv(3, 1.2, ctx.level)
      const radius = (2.1 + ctx.level * 0.24) * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'solarflare',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.5,
          pierce: 999,
          radius,
          color: 0xffb13d,
          burn: 2.5,
          knockback: 0,
          hitCooldown: 0.5,
        }),
      )
    },
    evolvesTo: 'coronalmass',
    evolveItem: 'solar_panel',
  },

  // ---- Nova Mortar: lobbed shell onto the toughest foe, big blast ----
  {
    id: 'novamortar',
    name: 'Nova Mortar',
    icon: '🌟',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `Lob a charged shell onto the toughest foe; detonates in a brilliant blast.`,
    baseCooldown: (l) => Math.max(1.2, 2.8 - 0.1 * l),
    aim: 'toughest',
    aimRange: 24,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      const tx = target ? target.x : world.player.x + world.rng.range(-6, 6)
      const tz = target ? target.z : world.player.z + world.rng.range(-6, 6)
      const base = lv(24, 7.5, ctx.level)
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'novamortar',
          behavior: 'lobbed',
          x: tx,
          z: tz,
          y: 13,
          vy: 0,
          damage: 0,
          crit: d.crit,
          life: 1.1,
          radius: 0.6,
          color: 0xffe27a,
          explodeRadius: (2.8 + ctx.level * 0.2) * ctx.stats.area,
          explodeDamage: d.amount,
          knockback: 1.8,
        }),
      )
    },
    evolvesTo: 'supernova',
    evolveItem: 'fusion_core',
  },

  // ---- Prism Beam: a fan of piercing light beams toward the nearest foe ----
  {
    id: 'prism',
    name: 'Prism Beam',
    icon: '🔺',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Split light into piercing beams aimed at the nearest foe. More beams as it levels.`,
    baseCooldown: (l) => Math.max(0.5, 1.2 - 0.06 * l),
    aim: 'nearest',
    aimRange: 26,
    fire(ctx) {
      const { world, target } = ctx
      const baseAngle = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.player.facing
      const count = 3 + Math.floor(ctx.level / 3) + ctx.stats.projectileCount
      const base = lv(8, 2.8, ctx.level)
      for (let i = 0; i < count; i++) {
        const spread = (i - (count - 1) / 2) * 0.16
        const a = baseAngle + spread
        const d = rollDamage(world, base)
        const speed = 24 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'prism',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 1.0 * ctx.stats.duration,
            pierce: 999,
            radius: 0.3 * ctx.stats.area,
            color: 0xff6ad5,
            knockback: 0.4,
          }),
        )
      }
    },
    evolvesTo: 'spectrum',
    evolveItem: 'crystal_lens',
  },

  // ---- Quasar Orb: a heavy slow-homing orb that erupts on impact ----
  {
    id: 'quasarorb',
    name: 'Quasar Orb',
    icon: '🔵',
    rarity: 'epic',
    maxLevel: 8,
    describe: () => `Release a heavy orb that slowly hunts the toughest foe, piercing and crushing all it meets.`,
    baseCooldown: (l) => Math.max(1.0, 2.2 - 0.1 * l),
    aim: 'toughest',
    aimRange: 26,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      const a = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.rng.next() * TAU
      const base = lv(20, 6.5, ctx.level)
      const d = rollDamage(world, base)
      const speed = 7 * ctx.stats.projectileSpeed
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'quasarorb',
          behavior: 'homing',
          x: world.player.x,
          z: world.player.z,
          vx: Math.cos(a) * speed,
          vz: Math.sin(a) * speed,
          speed,
          damage: d.amount,
          crit: d.crit,
          life: 3.5 * ctx.stats.duration,
          pierce: 999, // persistent re-hit orb (hitCooldown 0.4 cycles its hitSet) — "crushes all it meets"; pierce-count is inert when hitCooldown>0
          radius: 0.6 * ctx.stats.area,
          color: 0x6ad0ff,
          homingTurn: 3.5,
          knockback: 1.0,
          hitCooldown: 0.4,
        }),
      )
    },
    evolvesTo: 'quasarstorm',
    evolveItem: 'event_shard',
  },

  // ---- Photon Lance: rapid piercing light darts (fast sniper) ----
  {
    id: 'photonlance',
    name: 'Photon Lance',
    icon: '➡️',
    rarity: 'common',
    maxLevel: 8,
    describe: () => `Fire rapid darts of light that punch through a few foes. Fast and relentless.`,
    baseCooldown: (l) => Math.max(0.16, 0.5 - 0.04 * l),
    aim: 'nearest',
    aimRange: 30,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const base = lv(7, 2.4, ctx.level)
      const a = Math.atan2(target.z - world.player.z, target.x - world.player.x)
      const d = rollDamage(world, base)
      const speed = 34 * ctx.stats.projectileSpeed
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'photonlance',
          behavior: 'straight',
          x: world.player.x,
          z: world.player.z,
          vx: Math.cos(a) * speed,
          vz: Math.sin(a) * speed,
          speed,
          damage: d.amount,
          crit: d.crit,
          life: 1.2 * ctx.stats.duration,
          pierce: 2 + Math.floor(ctx.level / 3) + ctx.stats.projectileCount,
          radius: 0.26 * ctx.stats.area,
          color: 0xeaffff,
          knockback: 0.3,
        }),
      )
    },
    evolvesTo: 'photonstorm',
    evolveItem: 'light_amp',
  },

  // ---- Acid Spit: a homing glob that poisons whatever it hits ----
  {
    id: 'acidspit',
    name: 'Acid Spit',
    icon: '🟢',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Spit homing globs of acid that seek foes and corrode them with poison.`,
    baseCooldown: (l) => Math.max(0.7, 1.6 - 0.08 * l),
    aim: 'nearest',
    aimRange: 30,
    fire(ctx) {
      const { world, target } = ctx
      const count = 1 + Math.floor(ctx.level / 3) + ctx.stats.projectileCount
      const base = lv(6, 2.2, ctx.level)
      // launch toward the nearest foe (then home); fan out if several globs
      const baseAngle = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.rng.next() * TAU
      for (let i = 0; i < count; i++) {
        const a = baseAngle + (count > 1 ? (i - (count - 1) / 2) * 0.4 : 0)
        const d = rollDamage(world, base)
        const speed = 11 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'acidspit',
            behavior: 'homing',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 3 * ctx.stats.duration,
            pierce: 1,
            radius: 0.36 * ctx.stats.area,
            color: 0x9acd32,
            homingTurn: 5,
            poison: 3,
            knockback: 0.3,
          }),
        )
      }
    },
    evolvesTo: 'acidstorm',
    evolveItem: 'acid_vial',
  },

  // ---- Shock Nova: a kinetic blast that damages and hurls foes back ----
  {
    id: 'shocknova',
    name: 'Shock Nova',
    icon: '🟣',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `A kinetic shockwave bursts around you, blasting foes back with heavy knockback.`,
    baseCooldown: (l) => Math.max(0.8, 1.8 - 0.08 * l),
    fire(ctx) {
      const { world } = ctx
      const base = lv(6, 2.2, ctx.level)
      const radius = (2.6 + ctx.level * 0.26) * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'shocknova',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.3,
          pierce: 999,
          radius,
          color: 0xb98cff,
          knockback: 3.5,
          hitCooldown: 0.3,
        }),
      )
    },
    evolvesTo: 'pulsewave',
    evolveItem: 'kinetic_cell',
  },

  // ---- Star Shards: omnidirectional ricocheting shards ----
  {
    id: 'starshards',
    name: 'Star Shards',
    icon: '✦',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `Burst shards in all directions that ricochet between foes.`,
    baseCooldown: (l) => Math.max(0.7, 1.6 - 0.07 * l),
    fire(ctx) {
      const { world } = ctx
      const count = 4 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount
      const base = lv(7, 2.6, ctx.level)
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU
        const d = rollDamage(world, base)
        const speed = 17 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'starshards',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 2.6 * ctx.stats.duration,
            pierce: 0,
            bounce: 2 + Math.floor(ctx.level / 3),
            radius: 0.36 * ctx.stats.area,
            color: 0xfff0a0,
            knockback: 0.5,
          }),
        )
      }
    },
    evolvesTo: 'starfall',
    evolveItem: 'shard_matrix',
  },

  // ---- Cryo Bomb: lobbed shell that explodes into a freezing burst ----
  {
    id: 'cryobomb',
    name: 'Cryo Bomb',
    icon: '🧊',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `Lob a cryo shell at a random foe; bursts to freeze and slow everything caught.`,
    baseCooldown: (l) => Math.max(1.3, 2.8 - 0.1 * l),
    fire(ctx) {
      const { world } = ctx
      const target =
        randomEnemy(world, world.player.x, world.player.z, 20) ??
        nearestEnemy(world, world.player.x, world.player.z)
      const tx = target ? target.x : world.player.x + world.rng.range(-6, 6)
      const tz = target ? target.z : world.player.z + world.rng.range(-6, 6)
      const base = lv(16, 5, ctx.level)
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'cryobomb',
          behavior: 'lobbed',
          x: tx,
          z: tz,
          y: 13,
          vy: 0,
          damage: 0,
          crit: d.crit,
          life: 1.1,
          radius: 0.6,
          color: 0xaee5ff,
          explodeRadius: (3.0 + ctx.level * 0.22) * ctx.stats.area,
          explodeDamage: d.amount,
          slow: 0.7,
          freeze: 0.8 * ctx.stats.duration,
          knockback: 1.0,
        }),
      )
    },
    evolvesTo: 'absolutezero',
    evolveItem: 'cryo_canister',
  },

  // ---- Plasma Cutter: orbiting plasma blades that burn what they shred ----
  {
    id: 'plasmacutter',
    name: 'Plasma Cutter',
    icon: '🌗',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Searing plasma blades orbit fast and close, burning all they shred.`,
    baseCooldown: () => 1.5,
    fire(ctx) {
      refreshOrbitals(ctx.world, ctx.ws, {
        ownerWeapon: 'plasmacutter',
        behavior: 'spinner',
        count: 2 + Math.floor(ctx.level / 3) + ctx.stats.projectileCount,
        damageBase: lv(6, 2.4, ctx.level),
        radius: 0.5 * ctx.stats.area,
        color: 0xff8a3d,
        orbitRadius: 2.6 * ctx.stats.area,
        orbitSpeed: 8 * ctx.stats.projectileSpeed,
        burn: 2,
        knockback: 0.6,
        hitCooldown: 0.3,
      })
    },
    evolvesTo: 'plasmavortex',
    evolveItem: 'plasma_cell',
  },

  // ===== v11 content-fill: five more draftable base weapons (existing projectile behaviors, no new sim) =====
  // ---- Needler: rapid homing micro-darts that swarm the nearest foe ----
  {
    id: 'needler',
    name: 'Needler',
    icon: '🪡',
    rarity: 'common',
    maxLevel: 8,
    describe: () => `Spit a rapid stream of homing needles at the nearest foe. More needles as it levels.`,
    baseCooldown: (l) => Math.max(0.22, 0.7 - 0.05 * l),
    aim: 'nearest',
    aimRange: 26,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const count = 1 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount
      const base = lv(5, 1.8, ctx.level)
      const baseAngle = Math.atan2(target.z - world.player.z, target.x - world.player.x)
      for (let i = 0; i < count; i++) {
        const a = baseAngle + (count > 1 ? (i - (count - 1) / 2) * 0.22 : 0)
        const d = rollDamage(world, base)
        const speed = 16 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'needler',
            behavior: 'homing',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 2.4 * ctx.stats.duration,
            pierce: 1,
            radius: 0.28 * ctx.stats.area,
            color: 0xd0e0ff,
            homingTurn: 6,
            knockback: 0.2,
          }),
        )
      }
    },
  },

  // ---- War Glaive: a fan of glaives arc out and return, piercing all (forward, skill-aimed) ----
  {
    id: 'warglaive',
    name: 'War Glaive',
    icon: '🌗',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Hurl a fan of glaives that arc out and back, piercing the whole horde. More glaives as it levels.`,
    baseCooldown: (l) => Math.max(0.7, 1.5 - 0.06 * l),
    aim: 'nearest',
    aimRange: 24,
    fire(ctx) {
      const { world, target } = ctx
      const count = 2 + Math.floor(ctx.level / 3) + ctx.stats.projectileCount
      const base = lv(10, 3.4, ctx.level)
      const baseAngle = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.player.facing
      for (let i = 0; i < count; i++) {
        const a = baseAngle + (count > 1 ? (i - (count - 1) / 2) * 0.5 : 0)
        const d = rollDamage(world, base)
        const speed = 15 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'warglaive',
            behavior: 'boomerang',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 2.0 * ctx.stats.duration,
            pierce: 999,
            radius: 0.5 * ctx.stats.area,
            color: 0xaee5ff,
            knockback: 1.0,
            hitCooldown: 0.25,
            angle: a,
          }),
        )
      }
    },
  },

  // ---- Flak Cannon: lob a cluster of shells onto the toughest foe; each bursts in a small blast ----
  {
    id: 'flakcannon',
    name: 'Flak Cannon',
    icon: '💣',
    rarity: 'rare',
    maxLevel: 8,
    describe: () => `Lob a cluster of flak shells onto the toughest foe; each bursts in a small blast. More shells as it levels.`,
    baseCooldown: (l) => Math.max(1.1, 2.4 - 0.09 * l),
    aim: 'toughest',
    aimRange: 24,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      const cx = target ? target.x : world.player.x
      const cz = target ? target.z : world.player.z
      const shells = 2 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount
      const base = lv(12, 4, ctx.level)
      for (let i = 0; i < shells; i++) {
        const a = world.rng.next() * TAU
        const r = world.rng.range(0, 2.4)
        const d = rollDamage(world, base)
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'flakcannon',
            behavior: 'lobbed',
            x: cx + Math.cos(a) * r,
            z: cz + Math.sin(a) * r,
            y: 12,
            vy: 0,
            damage: 0,
            crit: d.crit,
            life: 1.0 + world.rng.range(0, 0.3),
            radius: 0.5,
            color: 0xffb14a,
            explodeRadius: (1.9 + ctx.level * 0.12) * ctx.stats.area,
            explodeDamage: d.amount,
            knockback: 1.0,
          }),
        )
      }
    },
  },

  // ---- Razor Ring: a tight ring of fast blades that orbit close and shred everything they touch ----
  {
    id: 'razorring',
    name: 'Razor Ring',
    icon: '⭕',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `A tight ring of fast blades orbits close, shredding anything that touches you. More blades as it levels.`,
    baseCooldown: () => 1.4,
    fire(ctx) {
      refreshOrbitals(ctx.world, ctx.ws, {
        ownerWeapon: 'razorring',
        behavior: 'spinner',
        count: 3 + Math.floor(ctx.level / 2) + ctx.stats.projectileCount,
        damageBase: lv(6, 2.2, ctx.level),
        radius: 0.42 * ctx.stats.area,
        color: 0xc6ccd8,
        orbitRadius: 2.2 * ctx.stats.area,
        orbitSpeed: 9 * ctx.stats.projectileSpeed,
        knockback: 0.5,
        hitCooldown: 0.3,
      })
    },
  },

  // ---- Gust Slash: a wide forward cleave that punches a crescent through the front line ----
  {
    id: 'gustslash',
    name: 'Gust Slash',
    icon: '🌬️',
    rarity: 'uncommon',
    maxLevel: 8,
    describe: () => `Cleave a wide crescent of wind in the way you face, slicing the whole front line. Cleaves both ways at lv 6.`,
    baseCooldown: (l) => Math.max(0.5, 1.1 - 0.05 * l),
    aim: 'forward',
    fire(ctx) {
      const { world } = ctx
      const face = world.player.facing
      const dirs = ctx.level >= 6 ? [face, face + Math.PI] : [face]
      const base = lv(9, 3.2, ctx.level)
      for (const a of dirs) {
        const d = rollDamage(world, base)
        const speed = 24 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'gustslash',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 0.32 * ctx.stats.duration,
            pierce: 999,
            radius: (1.1 + ctx.level * 0.05) * ctx.stats.area,
            color: 0xbfeaff,
            knockback: 1.1,
            hitCooldown: 0.15,
          }),
        )
      }
    },
  },

  // ===== Evolved weapons (hidden, obtained via evolution) =====
  {
    id: 'stormglaive',
    name: 'Storm Glaive',
    icon: '🌪️',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a storm of glaives circle out and back, chaining lightning.`,
    baseCooldown: () => 0.7,
    fire(ctx) {
      const { world } = ctx
      const count = 4 + ctx.stats.projectileCount
      const base = 26
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU + world.player.facing
        const d = rollDamage(world, base)
        const speed = 16 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'stormglaive',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            behavior: 'boomerang',
            damage: d.amount,
            crit: d.crit,
            life: 2.4 * ctx.stats.duration,
            pierce: 999,
            radius: 0.6 * ctx.stats.area,
            color: 0xbfe9ff,
            chain: 2,
            knockback: 1.4,
            hitCooldown: 0.22,
            angle: a,
          }),
        )
      }
    },
  },
  {
    id: 'gatling',
    name: 'Gatling Storm',
    icon: '🔥',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: an unrelenting hail of burning bullets.`,
    baseCooldown: () => 0.08,
    aim: 'nearest',
    aimRange: 26,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const base = 8
      const a =
        Math.atan2(target.z - world.player.z, target.x - world.player.x) +
        (world.rng.next() - 0.5) * 0.18
      const d = rollDamage(world, base)
      const speed = 30 * ctx.stats.projectileSpeed
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'gatling',
          x: world.player.x,
          z: world.player.z,
          vx: Math.cos(a) * speed,
          vz: Math.sin(a) * speed,
          speed,
          behavior: 'straight',
          damage: d.amount,
          crit: d.crit,
          life: 1.2,
          pierce: 2,
          radius: 0.3,
          color: 0xffae4a,
          burn: 1.5,
          knockback: 0.3,
        }),
      )
    },
  },
  {
    id: 'ringofsaturn',
    name: 'Ring of Saturn',
    icon: '💫',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a massive ring of orbs that obliterates everything around you.`,
    baseCooldown: () => 2.5,
    fire(ctx) {
      refreshOrbitals(ctx.world, ctx.ws, {
        ownerWeapon: 'ringofsaturn',
        behavior: 'orbit',
        count: 8 + ctx.stats.projectileCount,
        damageBase: 18,
        radius: 0.7 * ctx.stats.area,
        color: 0xffd27a,
        orbitRadius: 4.2 * ctx.stats.area,
        orbitSpeed: 4 * ctx.stats.projectileSpeed,
        knockback: 1,
        hitCooldown: 0.3,
      })
    },
  },
  {
    id: 'blizzard',
    name: 'Blizzard',
    icon: '🌨️',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a permanent storm of ice freezing and shredding all nearby.`,
    baseCooldown: () => 0.4,
    fire(ctx) {
      const { world } = ctx
      const base = 9
      const radius = 5 * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'blizzard',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.45,
          pierce: 999,
          radius,
          color: 0xcdeeff,
          slow: 0.7,
          freeze: 0.4,
          hitCooldown: 0.4,
        }),
      )
    },
  },
  {
    id: 'apocalypse',
    name: 'Apocalypse',
    icon: '🌋',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a relentless rain of meteors.`,
    baseCooldown: () => 0.8,
    fire(ctx) {
      const { world } = ctx
      const base = 30
      for (let k = 0; k < 2; k++) {
        const target = randomEnemy(world, world.player.x, world.player.z, 22)
        const tx = target ? target.x : world.player.x + world.rng.range(-8, 8)
        const tz = target ? target.z : world.player.z + world.rng.range(-8, 8)
        const d = rollDamage(world, base)
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'apocalypse',
            behavior: 'lobbed',
            x: tx,
            z: tz,
            y: 14,
            damage: 0,
            crit: d.crit,
            life: 1.0,
            radius: 0.6,
            color: 0xff5a2d,
            explodeRadius: 3.4 * ctx.stats.area,
            explodeDamage: d.amount,
            burn: 4,
            knockback: 1.6,
          }),
        )
      }
    },
  },
  {
    id: 'tempest',
    name: 'Tempest',
    icon: '🌩️',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: chain lightning that arcs across the entire battlefield.`,
    baseCooldown: () => 0.6,
    aim: 'nearest',
    aimRange: 24,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const base = 22
      const d = rollDamage(world, base)
      const p = world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'tempest',
          behavior: 'straight',
          x: target.x,
          z: target.z,
          vx: 0,
          vz: 0,
          speed: 0,
          damage: d.amount,
          crit: d.crit,
          life: 0.05,
          chain: 8 + ctx.stats.projectileCount,
          color: 0xbfe0ff,
          knockback: 0.4,
        }),
      )
      void p
    },
  },
  {
    id: 'pestilence',
    name: 'Pestilence',
    icon: '☣️',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a vast plague aura that melts the horde with stacking poison.`,
    baseCooldown: () => 0.5,
    fire(ctx) {
      const { world } = ctx
      const base = 7
      const radius = 4.6 * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'pestilence',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.5,
          pierce: 999,
          radius,
          color: 0x7ec850,
          poison: 4,
          knockback: 0,
          hitCooldown: 0.4,
        }),
      )
    },
  },
  {
    id: 'hydralash',
    name: 'Hydra Lash',
    icon: '🐍',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: whips lash out in every direction at blinding speed.`,
    baseCooldown: () => 0.35,
    fire(ctx) {
      const { world } = ctx
      const count = 8 + ctx.stats.projectileCount
      const base = 18
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU + world.player.facing * 0.3
        const d = rollDamage(world, base)
        const speed = 28 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'hydralash',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 0.24 * ctx.stats.duration,
            pierce: 999,
            radius: 1.5 * ctx.stats.area,
            color: 0xffd27a,
            knockback: 1.6,
            hitCooldown: 0.12,
          }),
        )
      }
    },
  },
  {
    id: 'minefield',
    name: 'Minefield',
    icon: '💣',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: blanket the ground in mines that detonate in fiery blasts.`,
    baseCooldown: () => 0.7,
    fire(ctx) {
      const { world } = ctx
      const count = 4 + ctx.stats.projectileCount
      const base = 24
      for (let i = 0; i < count; i++) {
        const a = world.rng.next() * TAU
        const r = 1 + world.rng.next() * 4
        const d = rollDamage(world, base)
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'minefield',
            behavior: 'lobbed',
            x: world.player.x + Math.cos(a) * r,
            z: world.player.z + Math.sin(a) * r,
            y: 6,
            damage: 0,
            crit: d.crit,
            life: 1.0,
            radius: 0.6,
            color: 0xff7a2d,
            explodeRadius: 2.6 * ctx.stats.area,
            explodeDamage: d.amount,
            burn: 3,
            knockback: 1.4,
          }),
        )
      }
    },
  },
  {
    id: 'pinball',
    name: 'Pinball',
    icon: '🔮',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a volley of balls that ricochet endlessly across the field.`,
    baseCooldown: () => 0.5,
    fire(ctx) {
      const { world } = ctx
      const count = 3 + ctx.stats.projectileCount
      const base = 16
      for (let i = 0; i < count; i++) {
        const a = world.rng.next() * TAU
        const d = rollDamage(world, base)
        const speed = 20 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'pinball',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 4 * ctx.stats.duration,
            pierce: 0,
            bounce: 10 + ctx.stats.projectileCount,
            radius: 0.45 * ctx.stats.area,
            color: 0xff9f4a,
            knockback: 0.7,
          }),
        )
      }
    },
  },
  {
    id: 'eventhorizon',
    name: 'Event Horizon',
    icon: '🌀',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: an immense black hole that swallows and annihilates the swarm.`,
    baseCooldown: () => 0.45,
    fire(ctx) {
      const { world } = ctx
      const base = 11
      const radius = 5.5 * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'eventhorizon',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.45,
          pierce: 999,
          radius,
          color: 0x9b6cff,
          pull: 14,
          knockback: 0,
          hitCooldown: 0.35,
        }),
      )
    },
  },
  {
    id: 'annihilator',
    name: 'Annihilator',
    icon: '🛰️',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a triple rail-beam that vaporizes everything in a wide arc.`,
    baseCooldown: () => 1.1,
    aim: 'toughest',
    aimRange: 44,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      const face = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.player.facing
      const base = 44
      for (let i = -1; i <= 1; i++) {
        const a = face + i * 0.22
        const d = rollDamage(world, base)
        const speed = 46 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'annihilator',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 1.6 * ctx.stats.duration,
            pierce: 999,
            radius: 0.7 * ctx.stats.area,
            color: 0x66ffff,
            knockback: 1.4,
          }),
        )
      }
    },
  },
  {
    id: 'miasma',
    name: 'Miasma',
    icon: '☠️',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: blankets the field in vast, lingering clouds of death.`,
    baseCooldown: () => 1.2,
    fire(ctx) {
      const { world } = ctx
      const base = 7
      for (let k = 0; k < 3; k++) {
        const target = randomEnemy(world, world.player.x, world.player.z, 22)
        const tx = target ? target.x : world.player.x + world.rng.range(-8, 8)
        const tz = target ? target.z : world.player.z + world.rng.range(-8, 8)
        const d = rollDamage(world, base)
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'miasma',
            behavior: 'straight',
            x: tx,
            z: tz,
            vx: 0,
            vz: 0,
            speed: 0,
            damage: d.amount,
            crit: d.crit,
            life: 5 * ctx.stats.duration,
            pierce: 999,
            radius: 3.4 * ctx.stats.area,
            color: 0x7ec850,
            poison: 4,
            knockback: 0,
            hitCooldown: 0.5,
          }),
        )
      }
    },
  },
  {
    id: 'glacialspear',
    name: 'Glacial Spear',
    icon: '🔱',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a colossal spear of ice that freezes and shatters everything it pierces.`,
    baseCooldown: () => 0.7,
    aim: 'nearest',
    aimRange: 26,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      const a = target
        ? Math.atan2(target.z - world.player.z, target.x - world.player.x)
        : world.player.facing
      const base = 30
      const d = rollDamage(world, base)
      const speed = 26 * ctx.stats.projectileSpeed
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'glacialspear',
          behavior: 'straight',
          x: world.player.x,
          z: world.player.z,
          vx: Math.cos(a) * speed,
          vz: Math.sin(a) * speed,
          speed,
          damage: d.amount,
          crit: d.crit,
          life: 1.8 * ctx.stats.duration,
          pierce: 999,
          radius: 0.7 * ctx.stats.area,
          color: 0xaee5ff,
          slow: 0.7,
          freeze: 1.0 * ctx.stats.duration,
          knockback: 1,
        }),
      )
    },
  },
  {
    id: 'coronalmass',
    name: 'Coronal Mass',
    icon: '🔥',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a vast solar corona that incinerates the whole horde.`,
    baseCooldown: () => 0.45,
    fire(ctx) {
      const { world } = ctx
      const base = 11
      const radius = 5.2 * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'coronalmass',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.45,
          pierce: 999,
          radius,
          color: 0xffb13d,
          burn: 4,
          knockback: 0,
          hitCooldown: 0.4,
        }),
      )
    },
  },
  {
    id: 'supernova',
    name: 'Supernova',
    icon: '💥',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a cluster of stellar shells rains down in chained detonations.`,
    baseCooldown: () => 0.9,
    fire(ctx) {
      const { world } = ctx
      const base = 30
      for (let k = 0; k < 3; k++) {
        const target = nearestEnemy(world, world.player.x, world.player.z, 24) ?? randomEnemy(world, world.player.x, world.player.z, 22)
        const tx = (target ? target.x : world.player.x) + world.rng.range(-3, 3)
        const tz = (target ? target.z : world.player.z) + world.rng.range(-3, 3)
        const d = rollDamage(world, base)
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'supernova',
            behavior: 'lobbed',
            x: tx,
            z: tz,
            y: 13,
            damage: 0,
            crit: d.crit,
            life: 1.0,
            radius: 0.6,
            color: 0xffe27a,
            explodeRadius: 3.4 * ctx.stats.area,
            explodeDamage: d.amount,
            burn: 3,
            knockback: 1.8,
          }),
        )
      }
    },
  },
  {
    id: 'spectrum',
    name: 'Spectrum',
    icon: '🌈',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a full spectrum of piercing beams fans out in every direction.`,
    baseCooldown: () => 0.5,
    fire(ctx) {
      const { world } = ctx
      const count = 12 + ctx.stats.projectileCount
      const base = 14
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU
        const d = rollDamage(world, base)
        const speed = 26 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'spectrum',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 1.0 * ctx.stats.duration,
            pierce: 999,
            radius: 0.34 * ctx.stats.area,
            color: 0xff6ad5,
            knockback: 0.4,
          }),
        )
      }
    },
  },
  {
    id: 'quasarstorm',
    name: 'Quasar Storm',
    icon: '🌐',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a barrage of crushing orbs that hunt across the battlefield.`,
    baseCooldown: () => 0.8,
    fire(ctx) {
      const { world } = ctx
      const count = 3 + ctx.stats.projectileCount
      const base = 24
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU
        const d = rollDamage(world, base)
        const speed = 9 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'quasarstorm',
            behavior: 'homing',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 3.5 * ctx.stats.duration,
            pierce: 999, // persistent re-hit orb (hitCooldown 0.4 cycles its hitSet); pierce-count is inert when hitCooldown>0
            radius: 0.65 * ctx.stats.area,
            color: 0x6ad0ff,
            homingTurn: 4.5,
            knockback: 1.2,
            hitCooldown: 0.4,
          }),
        )
      }
    },
  },
  {
    id: 'photonstorm',
    name: 'Photon Storm',
    icon: '☀️',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: an unending torrent of light darts that shred everything in line.`,
    baseCooldown: () => 0.06,
    aim: 'nearest',
    aimRange: 32,
    requiresTarget: true,
    fire(ctx) {
      const { world, target } = ctx
      if (!target) return
      const base = 9
      const a =
        Math.atan2(target.z - world.player.z, target.x - world.player.x) +
        (world.rng.next() - 0.5) * 0.12
      const d = rollDamage(world, base)
      const speed = 38 * ctx.stats.projectileSpeed
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'photonstorm',
          behavior: 'straight',
          x: world.player.x,
          z: world.player.z,
          vx: Math.cos(a) * speed,
          vz: Math.sin(a) * speed,
          speed,
          damage: d.amount,
          crit: d.crit,
          life: 1.2 * ctx.stats.duration,
          pierce: 4,
          radius: 0.3 * ctx.stats.area,
          color: 0xeaffff,
          knockback: 0.3,
        }),
      )
    },
  },
  {
    id: 'acidstorm',
    name: 'Acid Storm',
    icon: '🧫',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a relentless swarm of corrosive globs that melt the horde.`,
    baseCooldown: () => 0.35,
    fire(ctx) {
      const { world } = ctx
      const count = 3 + ctx.stats.projectileCount
      const base = 12
      for (let i = 0; i < count; i++) {
        const a = world.rng.next() * TAU
        const d = rollDamage(world, base)
        const speed = 13 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'acidstorm',
            behavior: 'homing',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 3 * ctx.stats.duration,
            pierce: 2,
            radius: 0.4 * ctx.stats.area,
            color: 0x9acd32,
            homingTurn: 6,
            poison: 5,
            knockback: 0.3,
          }),
        )
      }
    },
  },
  {
    id: 'pulsewave',
    name: 'Pulse Wave',
    icon: '🔊',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a massive kinetic pulse that flattens and scatters the swarm.`,
    baseCooldown: () => 0.7,
    fire(ctx) {
      const { world } = ctx
      const base = 16
      const radius = 5.6 * ctx.stats.area
      const d = rollDamage(world, base)
      world.spawnProjectile(
        makeProjectile({
          ownerWeapon: 'pulsewave',
          behavior: 'aura',
          x: world.player.x,
          z: world.player.z,
          damage: d.amount,
          crit: d.crit,
          life: 0.3,
          pierce: 999,
          radius,
          color: 0xb98cff,
          knockback: 6,
          hitCooldown: 0.3,
        }),
      )
    },
  },
  {
    id: 'starfall',
    name: 'Starfall',
    icon: '🌠',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a storm of shards that ricochet endlessly across the field.`,
    baseCooldown: () => 0.6,
    fire(ctx) {
      const { world } = ctx
      const count = 8 + ctx.stats.projectileCount
      const base = 14
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU
        const d = rollDamage(world, base)
        const speed = 18 * ctx.stats.projectileSpeed
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'starfall',
            behavior: 'straight',
            x: world.player.x,
            z: world.player.z,
            vx: Math.cos(a) * speed,
            vz: Math.sin(a) * speed,
            speed,
            damage: d.amount,
            crit: d.crit,
            life: 3 * ctx.stats.duration,
            pierce: 0,
            bounce: 8 + ctx.stats.projectileCount,
            radius: 0.42 * ctx.stats.area,
            color: 0xfff0a0,
            knockback: 0.6,
          }),
        )
      }
    },
  },
  {
    id: 'absolutezero',
    name: 'Absolute Zero',
    icon: '❄',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a barrage of cryo shells that locks the whole field in ice.`,
    baseCooldown: () => 1.0,
    fire(ctx) {
      const { world } = ctx
      const base = 20
      for (let k = 0; k < 3; k++) {
        const target = randomEnemy(world, world.player.x, world.player.z, 22)
        const tx = (target ? target.x : world.player.x) + world.rng.range(-4, 4)
        const tz = (target ? target.z : world.player.z) + world.rng.range(-4, 4)
        const d = rollDamage(world, base)
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'absolutezero',
            behavior: 'lobbed',
            x: tx,
            z: tz,
            y: 13,
            damage: 0,
            crit: d.crit,
            life: 1.0,
            radius: 0.6,
            color: 0xaee5ff,
            explodeRadius: 3.6 * ctx.stats.area,
            explodeDamage: d.amount,
            slow: 0.8,
            freeze: 1.2 * ctx.stats.duration,
            knockback: 1.0,
          }),
        )
      }
    },
  },
  {
    id: 'plasmavortex',
    name: 'Plasma Vortex',
    icon: '🌪',
    rarity: 'legendary',
    maxLevel: 6,
    hidden: true,
    describe: () => `EVOLVED: a roaring vortex of plasma blades that immolate everything near.`,
    baseCooldown: () => 0.9,
    fire(ctx) {
      const { world } = ctx
      const count = 6 + ctx.stats.projectileCount
      const base = 16
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU
        const d = rollDamage(world, base)
        world.spawnProjectile(
          makeProjectile({
            ownerWeapon: 'plasmavortex',
            behavior: 'spinner',
            x: world.player.x,
            z: world.player.z,
            damage: d.amount,
            crit: d.crit,
            life: 1.6 * ctx.stats.duration,
            pierce: 999,
            radius: 0.6 * ctx.stats.area,
            color: 0xff8a3d,
            angle: a,
            orbitRadius: 3.4 * ctx.stats.area,
            orbitSpeed: 9 * ctx.stats.projectileSpeed,
            burn: 3,
            knockback: 0.8,
            hitCooldown: 0.25,
          }),
        )
      }
    },
  },
]

export const WEAPONS: Record<string, WeaponDef> = {}
for (const w of WEAPON_LIST) WEAPONS[w.id] = w

/** Weapons that can appear in the level-up pool (non-hidden). */
export const POOL_WEAPONS = WEAPON_LIST.filter((w) => !w.hidden)
